// src/components/repasses/hooks/fetchCommissionData.ts

import { ComissionServices } from "../../../shared/services/api/comission/ComissionServices";
// Assumindo que você tem acesso a ComissionServices e suas funções (getComission, getComissionValuesDetail)
import { RowDataRepasse, CommissionDetail } from "../types"; // Certifique-se de que CommissionDetail está definido aqui
import { getDateRangeFromFilter } from "../utils/dateUtils";

// Simulação da função de serviço de detalhes (deve estar em ComissionServices)
const getComissionValuesDetail = async (clientId: string, dataini: string, datafim: string): Promise<CommissionDetail[] | Error> => {
	try {
		// Chamada adaptada do seu exemplo, visando obter uma lista de detalhes
		const result = await ComissionServices.getComissionValuesDetail(clientId, dataini, datafim);

		if (result instanceof Error || !result || !result.results) {
			// Tratamento para erro de API ou formato de resposta inesperado
			return new Error("Erro ao buscar detalhes de valores.");
		}

		// Assumindo que a resposta bem-sucedida é um objeto com a propriedade 'results' (o array de detalhes)
		return result.results as CommissionDetail[];
	} catch (error) {
		console.error("Erro na API de detalhes da comissão:", error);
		return new Error("Erro ao buscar detalhes de valores.");
	}
};

/**
 * Processa as linhas para buscar o ID da comissão e os detalhes de valores abertos
 * com base no CNPJ e no filtro de data.
 * @param data Os dados importados e validados.
 * @param toast A função toast.
 * @param dateFilter O filtro de data (ex: 'current_month').
 * @returns Um array de RowDataRepasse atualizado com idComissao e commissionDetails.
 */
export const fetchCommissionData = async (data: RowDataRepasse[], toast: any, dateFilter: string): Promise<RowDataRepasse[]> => {
	const { ini, fim } = getDateRangeFromFilter(dateFilter);
	const uniqueCnpjs = Array.from(new Set(data.map((row) => row.cnpjImobiliaria).filter(Boolean)));

	// Cache: { CNPJ: { clientId: string, details: CommissionDetail[] | null } | null }
	const commissionMetaCache: Record<string, { clientId: string; details: CommissionDetail[] | null } | null> = {};

	// Etapa 1: Busca encadeada para cada CNPJ único
	for (const cnpj of uniqueCnpjs) {
		// 1.1. Buscar o ID da comissão (clientId)
		const commissionResult = await ComissionServices.getComission(1, 1, cnpj, ini, fim, "");

		if (commissionResult instanceof Error || commissionResult.data.length === 0) {
			commissionMetaCache[cnpj] = null;
			continue;
		}

		const clientId = commissionResult.data[0].clientId; // Usamos clientId como o ID

		// 1.2. Usar o clientId para buscar os Detalhes de Valores Abertos
		const detailsResult = await getComissionValuesDetail(clientId, ini, fim);

		if (detailsResult instanceof Error) {
			// Falha na busca de detalhes. Armazenamos o clientId, mas não os detalhes.
			commissionMetaCache[cnpj] = { clientId, details: null };
			toast.present({ type: "warning", messageOrContent: `Detalhes de valores não encontrados para ID ${clientId}.` });
		} else {
			// Sucesso: Armazenamos o clientId e os detalhes
			commissionMetaCache[cnpj] = { clientId, details: detailsResult };
		}
	}

	// Etapa 2: Mapear os dados de volta com os IDs e Detalhes
	return data.map((row) => {
		const cached = commissionMetaCache[row.cnpjImobiliaria];
		let newStatus = row.status;
		let newMensagem = row.mensagem;

		// Se a validação local já marcou como inválido, não alteramos o status
		if (newStatus === "inválido") {
			return row;
		}

		if (!row.cnpjImobiliaria) {
			newStatus = "inválido";
			newMensagem += (newMensagem ? " | " : "") + "CNPJ da Imobiliária ausente.";
		} else if (!cached) {
			// Falhou na Etapa 1.1
			newStatus = "inválido";
			newMensagem += (newMensagem ? " | " : "") + `Comissão não encontrada para CNPJ ${row.cnpjImobiliaria} no período.`;
		} else if (!cached.details || cached.details.length === 0) {
			// Falhou na Etapa 1.2 (Não encontrou detalhes)
			newStatus = "inválido";
			newMensagem += (newMensagem ? " | " : "") + `Detalhes de valores abertos não encontrados para o CNPJ ${row.cnpjImobiliaria}.`;
		} else {
			newMensagem = "Dados da comissão e detalhes encontrados."; // Sucesso
		}

		return {
			...row,
			idComissao: cached?.clientId || null, // clientId
			commissionDetails: cached?.details || null, // Anexa os detalhes
			status: newStatus,
			mensagem: newMensagem,
		} as RowDataRepasse;
	});
};
